import React from 'react'
import { Link } from '@src/components/Link'
import CaretLeftIcon from '@icons/CaretLeftIcon'
import CaretRightIcon from '@icons/CaretRightIcon'
import { Theme, Box, Tab, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { TabContext, TabList } from '@material-ui/lab'
import { getValidSearch } from '@src/utils/searchUtils'
import { searchResultsRoute, useRouter } from '@src/routes'
import HandleMarkup from '@src/components/HandleMarkup'
import { sendBreadcrumbsEvent } from '@src/utils/analytics'
import { FormattedMessage } from 'react-intl'

export interface PDPBreadcrumbItem {
  url?: string
  label?: React.ReactNode
  gaLabel?: string
}

export interface PDPBreadcrumbProps {
  productCategories: ({
    __typename?: string
    category: string
    url: string
  } | null)[]
}

const useStyle = makeStyles((theme: Theme) => {
  return {
    backButton: {
      alignItems: 'center',
      color: theme.palette.primary.main,
      display: 'flex',
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
      marginRight: theme.spacing(5),
      textTransform: 'capitalize',
      paddingLeft: theme.spacing(0),
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    backIcon: {
      display: 'flex',
      fontSize: theme.typography.pxToRem(12),
      marginRight: theme.spacing(2.5),
    },
    noLinkLabel: {
      fontWeight: theme.typography.fontWeightRegular,
      fontSize: theme.typography.pxToRem(12),
    },
    searchResultsNav: {
      alignItems: 'center',
      display: 'flex',
      height: theme.spacing(5),
      maxWidth: '100%',
    },
    rightIcon: {
      display: 'flex',
      fontSize: theme.typography.pxToRem(12),
      marginLeft: theme.spacing(2.5),
    },
    tab: {
      fontWeight: theme.typography.fontWeightMedium,
      padding: theme.spacing(0),
      textAlign: 'start',
      textTransform: 'capitalize',
      '&.MuiTab-root': {
        minHeight: theme.spacing(11.8),
        minWidth: theme.spacing(0),
      },
      '&:last-child': {
        color: theme.palette.text.primary,
        cursor: 'default',
      },
      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.pxToRem(18),
        fontWeight: theme.typography.fontWeightBold,
      },
    },
    tabLabel: {
      alignItems: 'center',
      display: 'flex',
    },
    tabs: {
      color: theme.palette.primary.main,
      flexGrow: 1,
      opacity: 1,
      '& .Mui-selected': {
        color: theme.palette.common.black,
        opacity: 1,
      },
      '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.common.black,
        height: theme.spacing(1),
      },
      '& .MuiTab-textColorInherit': {
        opacity: 1,
      },
      '& .MuiTabs-scroller': {
        [theme.breakpoints.down('sm')]: {
          borderBottom: `1px solid ${theme.palette.brand.main}`,
          paddingLeft: theme.spacing(2),
        },
      },
      '& .MuiTab-wrapper': {
        fontSize: theme.typography.pxToRem(12),
        marginRight: theme.spacing(3),
      },
      [theme.breakpoints.up('sm')]: {
        minHeight: theme.spacing(12),
      },
      [theme.breakpoints.down('sm')]: {
        '& .MuiTab-root': {
          marginRight: theme.spacing(0),
        },
      },
    },
    topSection: {
      alignItems: 'center',
      paddingBottom: theme.spacing(1),
      width: '100%',
    },
  }
})

const PDPBreadcrumb: React.FC<PDPBreadcrumbProps> = ({ productCategories }) => {
  const internalClass = useStyle()
  const { query } = useRouter()
  const productCategoryLink = productCategories?.[0]?.url
  const productCategoryLabel = productCategories?.[0]?.category
  const querySearchTerm = localStorage?.getItem('searchedfor')
    ? JSON.parse(localStorage?.getItem('searchedfor') || '')
    : ''

  const searchPath = querySearchTerm
    ? `${searchResultsRoute.searchResults(querySearchTerm)}${getValidSearch({
        ...{ term: querySearchTerm },
      })}`
    : searchResultsRoute.index()

  const productsBreadcrumb = {
    url: '/products',
    label: 'Products',
    gaLabel: 'Products',
  }

  const breadCrumbItems = productCategoryLink
    ? [
        productsBreadcrumb,
        {
          url: productCategoryLink,
          label: <HandleMarkup value={productCategoryLabel} />,
          gaLabel: productCategoryLabel,
        },
        {
          label: <HandleMarkup value={query.productKey?.toUpperCase()} />,
          gaLabel: query.productKey?.toUpperCase(),
        },
      ]
    : [
        productsBreadcrumb,
        {
          label: <HandleMarkup value={query.productKey?.toUpperCase()} />,
          gaLabel: query.productKey?.toUpperCase(),
        },
      ]

  const eventLabel = breadCrumbItems
    .filter((item) => item.gaLabel)
    .map((item) => item?.gaLabel?.trim())

  const analyticsShowBack = querySearchTerm !== ''

  return (
    <Box className={internalClass.topSection} data-testid="pdp-breadcrumbs">
      <Box py={6} className={internalClass.searchResultsNav}>
        {querySearchTerm !== '' && (
          <Link href={searchPath} passHref>
            <a
              aria-hidden
              className={internalClass.backButton}
              onClick={() =>
                sendBreadcrumbsEvent(
                  'back',
                  eventLabel,
                  searchPath,
                  analyticsShowBack
                )
              }
            >
              <CaretLeftIcon
                color="primary"
                className={internalClass.backIcon}
              />
              <FormattedMessage id="BACK" defaultMessage="Back" />
            </a>
          </Link>
        )}
        <TabContext value="">
          <TabList
            aria-label="pdp-breadcrumb-tabs"
            className={internalClass.tabs}
            data-testid="breadcrumb-tabs"
            TabIndicatorProps={{
              style: { display: 'none' },
            }}
            variant="scrollable"
          >
            {breadCrumbItems.map(({ label, gaLabel, url }, i) => (
              <Tab
                label={
                  url ? (
                    <Box className={internalClass.tabLabel}>
                      <Link href={url} passHref>
                        <a
                          aria-hidden
                          data-testid={`breadcrumb-link-${gaLabel}`}
                          onClick={() =>
                            sendBreadcrumbsEvent(
                              gaLabel,
                              eventLabel,
                              url,
                              analyticsShowBack
                            )
                          }
                        >
                          {label}
                        </a>
                      </Link>
                      {i !== breadCrumbItems.length - 1 && (
                        <CaretRightIcon
                          color="disabled"
                          className={internalClass.rightIcon}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography className={internalClass.noLinkLabel}>
                      {label}
                    </Typography>
                  )
                }
                value=""
                className={internalClass.tab}
                key={`pdp-breadcrumb-tab-${gaLabel}-${i}`}
              />
            ))}
          </TabList>
        </TabContext>
      </Box>
    </Box>
  )
}

export default PDPBreadcrumb
